import { useEffect, useState } from 'react';

import { BRAIN_TRACE_PATHS } from './brainTracePaths';

const AI_BRAIN_IMG = `${process.env.PUBLIC_URL}/images/AI%20Brain.png`;

function BrainGradientStops({ animate }: { animate: boolean }) {
  if (!animate) {
    return (
      <>
        <stop offset="0%" stopColor="#7ec4ff" />
        <stop offset="40%" stopColor="#2b93f0" />
        <stop offset="75%" stopColor="#00c2ab" />
        <stop offset="100%" stopColor="#5effe8" />
      </>
    );
  }

  return (
    <>
      <stop offset="0%" stopColor="#7ec4ff">
        <animate
          attributeName="stop-color"
          values="#7ec4ff;#5effe8;#7ec4ff"
          dur="4s"
          repeatCount="indefinite"
        />
      </stop>
      <stop offset="40%" stopColor="#2b93f0">
        <animate
          attributeName="stop-color"
          values="#2b93f0;#00c2ab;#2b93f0"
          dur="4s"
          repeatCount="indefinite"
        />
      </stop>
      <stop offset="75%" stopColor="#00c2ab">
        <animate
          attributeName="stop-color"
          values="#00c2ab;#2b93f0;#00c2ab"
          dur="4s"
          repeatCount="indefinite"
        />
      </stop>
      <stop offset="100%" stopColor="#5effe8">
        <animate
          attributeName="stop-color"
          values="#5effe8;#7ec4ff;#5effe8"
          dur="4s"
          repeatCount="indefinite"
        />
      </stop>
      <animateTransform
        attributeName="gradientTransform"
        type="rotate"
        from="0 0.5 0.5"
        to="360 0.5 0.5"
        dur="10s"
        repeatCount="indefinite"
      />
    </>
  );
}

type TravelingDotProps = {
  pathD: string;
  duration: number;
  begin?: number;
  r?: number;
  fill: string;
};

function TravelingDot({ pathD, duration, begin = 0, r = 3.5, fill }: TravelingDotProps) {
  return (
    <circle r={r} fill={fill} filter="url(#hiwBrainDotGlow)">
      <animateMotion
        dur={`${duration}s`}
        begin={`${begin}s`}
        repeatCount="indefinite"
        path={pathD}
        calcMode="linear"
      />
    </circle>
  );
}

/** AI brain line art with animated teal–blue border glow (SVG mask + gradient). */
export default function AiBrainVisual() {
  const [motionOk, setMotionOk] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setMotionOk(!mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return (
    <div className="hiw-ai-brain-visual" role="img" aria-label="AI neural engine illustration">
      <svg
        className="hiw-ai-brain-visual__svg"
        viewBox="0 0 400 400"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <defs>
          <linearGradient id="hiwBrainOutlineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <BrainGradientStops animate={motionOk} />
          </linearGradient>

          <radialGradient id="hiwBrainDotFill" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="45%" stopColor="#5effe8" />
            <stop offset="100%" stopColor="#00c2ab" />
          </radialGradient>

          <filter id="hiwBrainMaskBoost" colorInterpolationFilters="sRGB">
            <feColorMatrix
              type="matrix"
              values="4 0 0 0 -0.35  0 4 0 0 -0.35  0 0 4 0 -0.35  0 0 0 1 0"
            />
          </filter>

          <filter id="hiwBrainBorderGlow" x="-35%" y="-35%" width="170%" height="170%">
            <feGaussianBlur stdDeviation={motionOk ? 4 : 5} result="blur">
              {motionOk ? (
                <animate
                  attributeName="stdDeviation"
                  values="3;8;3"
                  dur="2.8s"
                  repeatCount="indefinite"
                />
              ) : null}
            </feGaussianBlur>
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="hiwBrainLineGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.2" result="soft" />
            <feMerge>
              <feMergeNode in="soft" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="hiwBrainDotGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <mask id="hiwBrainLineMask" maskUnits="userSpaceOnUse" x="0" y="0" width="400" height="400">
            <image
              href={AI_BRAIN_IMG}
              x="0"
              y="0"
              width="400"
              height="400"
              preserveAspectRatio="xMidYMid meet"
              filter="url(#hiwBrainMaskBoost)"
            />
          </mask>
        </defs>

        <rect
          className="hiw-ai-brain-visual__glow"
          width="400"
          height="400"
          fill="url(#hiwBrainOutlineGrad)"
          mask="url(#hiwBrainLineMask)"
          filter="url(#hiwBrainBorderGlow)"
        />
        <rect
          className="hiw-ai-brain-visual__lines"
          width="400"
          height="400"
          fill="url(#hiwBrainOutlineGrad)"
          mask="url(#hiwBrainLineMask)"
          filter="url(#hiwBrainLineGlow)"
        />

        {motionOk ? (
          <g className="hiw-ai-brain-visual__traces" mask="url(#hiwBrainLineMask)">
            {BRAIN_TRACE_PATHS.map((trace) => (
              <g key={trace.id}>
                <path
                  className="hiw-ai-brain-visual__trace"
                  d={trace.d}
                  fill="none"
                  stroke="url(#hiwBrainOutlineGrad)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.35"
                  pathLength={100}
                  strokeDasharray="2 98"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from="0"
                    to="-100"
                    dur={`${trace.duration}s`}
                    begin={`${trace.begin ?? 0}s`}
                    repeatCount="indefinite"
                  />
                </path>
                <TravelingDot
                  pathD={trace.d}
                  duration={trace.duration}
                  begin={trace.begin}
                  fill="url(#hiwBrainDotFill)"
                />
                <TravelingDot
                  pathD={trace.d}
                  duration={trace.duration}
                  begin={(trace.begin ?? 0) + trace.duration / 2}
                  r={2.5}
                  fill="#7ec4ff"
                />
              </g>
            ))}
          </g>
        ) : null}
      </svg>
      <div className="hiw-ai-brain-visual__chip">
        <span>AI</span>
      </div>
    </div>
  );
}
